import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { fetchMyAuthorization } from '@/features/authorization/api'
import { refreshSession, setSessionHandlers } from '@/internationalization/api-client'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import type { Tenant, User } from '@/types'

let handlersWired = false

/**
 * Route guard, and the one place a session is restored after a reload.
 *
 * The access token is held in memory only, so a refresh of the browser starts
 * with no token and an HttpOnly refresh cookie. Rendering the redirect to
 * /login before that cookie has been tried would sign everybody out on every
 * reload, so the guard waits for the first attempt to settle.
 *
 * The redirect carries the attempted path so sign-in returns the user where
 * they were going -- a link to a meeting that dumps you on the dashboard after
 * login is a link that did not work.
 *
 * This is a UX guard, never a security boundary. Authorisation is the API's
 * job; hiding a route only hides the button.
 */
export function RequireAuth() {
  const t = useT()
  const token = useSessionStore((s) => s.token)
  const restored = useSessionStore((s) => s.restored)
  const permissions = useSessionStore((s) => s.permissions)
  const tenantId = useSessionStore((s) => s.tenant?.id)
  const location = useLocation()
  const attempted = useRef(false)

  /**
   * Load the permission set whenever it is unknown.
   *
   * Three ways it becomes unknown, and `/session` covers none of them: a
   * RELOAD restores from the refresh cookie, which returns a token and a user
   * but no permissions; a WORKSPACE SWITCH clears them, because one person can
   * be an admin at one customer and a viewer at another; and a fresh sign-in
   * that took the SSO path lands here without them.
   *
   * ON FAILURE IT LEAVES THEM NULL, which every consumer reads as denied. A
   * fallback of "assume the last known set" or "assume none is a bug, show
   * everything" would put the admin panel in front of whoever the request
   * failed for.
   */
  useEffect(() => {
    if (!token || !tenantId || permissions !== null) return
    let cancelled = false
    void fetchMyAuthorization()
      .then((mine) => {
        if (cancelled) return
        const session = useSessionStore.getState()
        // Capabilities ride along on the same response deliberately: they are
        // needed by the same first render, and a second round trip for them
        // would have the gated controls appear a beat after the rest.
        session.setCapabilities(mine.capabilities ?? {})
        session.setPermissions(mine.permissions)
        session.setCanAccessVocabulary(mine.canAccessVocabulary ?? false)
        session.setCanAccessFeedbackInbox(mine.canAccessFeedbackInbox ?? false)
      })
      .catch(() => {
        /* stays null: denied until we can establish otherwise */
      })
    return () => {
      cancelled = true
    }
  }, [token, tenantId, permissions])

  useEffect(() => {
    if (!handlersWired) {
      handlersWired = true
      const { signIn, signOut } = useSessionStore.getState()
      setSessionHandlers({
        onRefreshed: (session) =>
          signIn({
            token: session.token,
            user: session.user as User,
            tenant: session.tenant as Tenant,
          }),
        onLost: signOut,
      })
    }
    if (attempted.current || token) return
    attempted.current = true
    void refreshSession().finally(() => useSessionStore.getState().markRestored())
  }, [token])

  if (!token) {
    if (!restored) {
      return (
        <div className="flex min-h-[50vh] items-center justify-center text-fg-muted">
          <span className="sr-only">{t('nav.restoringSession')}</span>
        </div>
      )
    }
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <Outlet />
}
